import { SxProps, TextField, Theme } from '@mui/material';
import { ReactNode } from 'react';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  PathValue,
  Validate,
} from 'react-hook-form';

export interface RhfTextFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends Pick<ControllerProps<TFieldValues, TName>, 'control' | 'name'> {
  label: string;
  endAdornment?: ReactNode;
  helperText?: ReactNode;
  type?: string;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;

  /**
   * Format the form state value before it is rendered. Useful for e.g. currency
   * values.
   */
  valueToString?(value: PathValue<TFieldValues, TName>): string;

  /**
   * Parse the user input before it is stored in the form state. Useful for e.g.
   * number parsing.
   */
  stringToValue?(input: string): PathValue<TFieldValues, TName> | undefined;

  /**
   * Return an error message or `undefined` if the field has no errors.
   */
  validate?: Validate<PathValue<TFieldValues, TName>>;
  onChange?: (input: string) => void;

  sx?: SxProps<Theme>;
  autoFocus?: boolean;
}

export function RhfTextField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  endAdornment,
  helperText,
  disabled = false,
  type = 'text',
  multiline = false,
  rows,
  autoFocus = false,
  valueToString = identity,
  stringToValue = identity,
  validate,
  onChange,
  sx,
}: RhfTextFieldProps<TFieldValues, TName>) {
  return (
    <Controller
      control={control}
      name={name}
      rules={{ validate }}
      render={({ field, fieldState }) => {
        return (
          <TextField
            {...field}
            type={type}
            autoFocus={autoFocus}
            value={valueToString(field.value) ?? ''}
            onChange={(e) => {
              field.onChange(stringToValue(e.target.value) ?? '');
              onChange?.(e.target.value);
            }}
            label={label}
            InputProps={{ endAdornment }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message ?? helperText}
            InputLabelProps={type !== 'text' ? { shrink: true } : undefined}
            disabled={disabled}
            multiline={multiline}
            rows={rows}
            sx={sx}
          />
        );
      }}
    />
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function identity(value: any): any {
  return value;
}
